import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Escape closes the menu, and focus returns to the control that opened it,
  // so a keyboard user is never stranded behind the overlay.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isMobileMenuOpen && toggleRef.current) {
      toggleRef.current.focus();
    }
    wasOpenRef.current = isMobileMenuOpen;
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/#book' },
  ];

  const scrollToSection = (e, path) => {
    if (path.startsWith('/#')) {
      e.preventDefault();
      const sectionId = path.substring(2);
      if (location.pathname === '/') {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = path;
      }
    }
  };

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-2xl border-b border-black/5 py-4 shadow-sm'
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <nav aria-label="Main" className="flex items-center justify-between">
            {/* Logo — white on transparent hero, black when scrolled */}
            <Link to="/" data-testid="logo" className="flex items-center gap-3 group">
              <img
                src={isScrolled ? '/brand/nci-logo-black.png' : '/brand/nci-logo-white.png'}
                alt="New Creative Industries"
                className="h-20 md:h-24 w-auto transition-all duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={(e) => scrollToSection(e, link.path)}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link text-sm uppercase tracking-[0.2em] font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#17798A]'
                      : isScrolled
                        ? 'text-[#444444] hover:text-[#111111]'
                        : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <Link
              to="/#book"
              onClick={(e) => scrollToSection(e, '/#book')}
              data-testid="header-cta"
              className="hidden md:inline-flex items-center justify-center px-6 py-3 text-sm font-bold uppercase tracking-widest text-white bg-[#17798A] hover:bg-[#12626F] transition-all duration-300 rounded-lg"
            >
              Book Now
            </Link>

            {/* Mobile Menu Button */}
            <button
              ref={toggleRef}
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
              className={`md:hidden p-2 transition-colors ${isScrolled ? 'text-[#111111]' : 'text-white'}`}
            >
              {isMobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu — the wrapper is always mounted so aria-controls on the
          toggle always has a target, even while the panel itself is unmounted. */}
      <div id="mobile-nav">
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Decorative scrim. Keyboard users dismiss with Escape (above),
                so this does not need to be focusable. */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute right-0 top-0 bottom-0 w-72 bg-white border-l border-black/5 pt-24 px-8 shadow-2xl">
              <nav aria-label="Mobile" className="flex flex-col gap-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={(e) => scrollToSection(e, link.path)}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`text-lg uppercase tracking-[0.15em] font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'text-[#17798A]'
                        : 'text-[#444444] hover:text-[#111111]'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/#book"
                  onClick={(e) => scrollToSection(e, '/#book')}
                  data-testid="mobile-cta"
                  className="mt-4 inline-flex items-center justify-center px-6 py-4 text-sm font-bold uppercase tracking-widest text-white bg-[#17798A] hover:bg-[#12626F] transition-all duration-300 rounded-lg"
                >
                  Book Now
                </Link>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
};
